import { useSearchParams } from "react-router";
import { Header, SearchSurface } from "./_components/empty-state";
import { MatchesPanel, ResultsToolbar } from "./_components/results-layout";

export default function ScreeningPage() {
  return <ScreeningShell />;
}

/**
 * Single shell that holds both the empty (centered) and results (top-aligned)
 * states. The `SearchSurface` stays mounted across the transition so users see
 * it slide upward instead of unmount/remount-flashing. Header / Toolbar /
 * MatchesPanel each fade in or out via opacity + max-height transitions.
 *
 * Width: empty state caps content at 1159px (centered); results state lets
 * content fill the column (max-w-full) so the surface and matches stretch
 * to the page edges minus the outer 32px (px-8) margins. The max-width
 * transition is animated so the widening feels continuous with the
 * upward-slide.
 *
 * Spacing note: the outer container intentionally has **no `gap-*`** — when
 * a flex child is collapsed via `max-h-0 + opacity-0` it would still consume
 * one `gap` increment on each side. Spacing is applied as **per-element
 * `margin-bottom` / `margin-top`** that goes to 0 when the element is
 * hidden, so collapsed siblings contribute zero space.
 */
function ScreeningShell() {
  const [params] = useSearchParams();
  const q = (params.get("q") ?? "").trim();
  const identifier = (params.get("identifier") ?? "").trim();
  const batchId = params.get("batch")?.trim() || undefined;
  const isResults = q.length > 0 || identifier.length > 0;

  // Width applied to every section wrapper. Same string in both branches so
  // browsers can interpolate `max-width` smoothly between 1159px and 100%.
  const widthClass = isResults ? "max-w-full" : "max-w-[1159px]";

  return (
    <div
      className={[
        "flex min-h-screen flex-col bg-white px-4 pb-4 sm:px-8",
        "transition-[padding-top] duration-500 ease-out",
        // Phone/tablet (<lg): static `pt-4` matches AGENTS body padding —
        // content starts at the top. Desktop (>=lg) keeps the slide-up
        // animation: empty pushes the search row to 20vh from the top, results
        // animates it back up. On lg the results top gap is `lg:pt-0` here and
        // the 16px breathing room lives on the sticky chrome below (`lg:pt-4`),
        // so it survives when the chrome pins to top:0 — the outer pt would
        // otherwise scroll away and jam the breadcrumb against the viewport top.
        // The `sm:pt-[28vh]` step was too aggressive (`sm:` 640+ left a 247px
        // void at 558x884 tablets).
        isResults ? "pt-4 lg:pt-0" : "pt-4 lg:pt-[20vh]",
      ].join(" ")}
    >
      {/* CHROME — Toolbar + Header + SearchSurface group. On lg+ in results
          state this pins to the top of the AppLayout content scroller so
          users can update filters without scrolling back up. Edge-to-edge
          bg-white (negative margin trick) covers matches when scrolled. z-30
          stays above the matches table's `z-10` column-header sticky stripe.
          `lg:pt-4` gives the pinned chrome the same 16px top breathing room it
          has at rest (the outer's results top padding is `lg:pt-0`) — without
          it the chrome pins flush at top:0 and the breadcrumb jams against the
          viewport top. The pt is inside the bg-white box, so matches scrolling
          underneath never peek through above the chrome.
          Below lg (phone + tablet), sticky is NOT applied — the chrome with
          expanded filter cards on phone would be 99% of viewport height
          (755/759 measured at 375x812) and would cover the entire matches
          table. Phone keeps the natural scroll-with-page behavior; the
          earlier `Show filters` toggle (`@3xl:hidden`) already keeps the
          chrome short by default in populated state.
          In empty state, sticky is a no-op even on lg (no scroll behind the
          chrome until the slide-up transition completes). */}
      <div
        className={[
          "-mx-4 sm:-mx-8 px-4 sm:px-8 bg-white",
          isResults ? "lg:sticky lg:top-0 lg:z-30 lg:pt-4 lg:pb-2" : "",
        ].join(" ")}
      >
        {/* Toolbar — only present in results state */}
        <div
          className={[
            "mx-auto w-full overflow-hidden",
            widthClass,
            "transition-[max-height,opacity,margin,max-width] duration-500 ease-out",
            isResults ? "max-h-[80px] opacity-100 mb-2" : "max-h-0 opacity-0 mb-0",
          ].join(" ")}
          aria-hidden={!isResults}
        >
          <ResultsToolbar query={q} batchId={batchId} />
        </div>

        {/* Empty-state header — collapses in results state */}
        <div
          className={[
            "mx-auto w-full overflow-hidden",
            widthClass,
            "transition-[max-height,opacity,margin,max-width] duration-500 ease-out",
            isResults ? "max-h-0 opacity-0 mb-0" : "max-h-[200px] opacity-100 mb-2",
          ].join(" ")}
          aria-hidden={isResults}
        >
          <Header />
        </div>

        {/* Search surface — stays visible in both empty and results states so
            users can see the active query + filters and re-run a search
            without leaving the results page. */}
        <div
          className={[
            "mx-auto w-full",
            widthClass,
            "transition-[max-width] duration-500 ease-out",
          ].join(" ")}
        >
          <SearchSurface initialQuery={q} isResults={isResults} />
        </div>
      </div>

      {/* Matches panel — keyed by q so the skeleton restarts on every search.
          Scrolls beneath the sticky chrome above; mt-2 keeps the visual gap
          consistent with the previous mb-2 on SearchSurface wrapper. */}
      <div
        className={[
          "mx-auto flex w-full flex-col overflow-hidden",
          widthClass,
          "transition-[max-height,opacity,margin,max-width] duration-500 ease-out",
          isResults ? "max-h-[3000px] opacity-100 mt-2" : "max-h-0 opacity-0 mt-0",
        ].join(" ")}
        aria-hidden={!isResults}
      >
        {isResults && <MatchesPanel key={`${q}|${identifier}`} query={q} />}
      </div>
    </div>
  );
}
